import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import { dicomColorToRgbString } from '~/utils/color';
import { labelSx, mutedNoteSx } from './constraintStyles';

interface OutsideConstraintSectionProps {
    /**
     * The modifier is off. Its settings show but are held, so nothing can
     * be set that does not apply: turn the modifier on to change them.
     */
    disabled: boolean;
    /** Structures selectable as protected. */
    candidates: readonly RoiReadModel[];
    /** The active Inside bound — cannot also be a protected structure. */
    activeBoundingRoi: number | null;
    /** roiNumbers currently selected as protected. */
    protectedRoiNumbers: number[];
    /** Whether the set has any approved structure eligible for protection. */
    hasApproved: boolean;
    /** Whether every approved structure is currently protected. */
    allApprovedProtected: boolean;
    /** Whether some (but not all) approved structures are protected. */
    someApprovedProtected: boolean;
    /**
     * Erase mode is active: the selection is persisted but not applied to the
     * stroke. Controls stay interactive; a note explains they are dormant now.
     */
    erasing: boolean;
    onToggleProtected: (roiNumber: number, checked: boolean) => void;
    onToggleProtectAllApproved: (checked: boolean) => void;
}

/**
 * The settings of the Outside (collision) constraint, which subtracts a set
 * of protected structures from the stroke so it cannot overwrite a
 * neighbour: the structures to protect. It is presentational: all state lives in
 * the parent orchestrator.
 */
export default function OutsideConstraintSection({
    disabled,
    candidates,
    activeBoundingRoi,
    protectedRoiNumbers,
    hasApproved,
    allApprovedProtected,
    someApprovedProtected,
    erasing,
    onToggleProtected,
    onToggleProtectAllApproved,
}: OutsideConstraintSectionProps) {
    return (
        <>
            {erasing && (
                <Typography sx={mutedNoteSx}>
                    <FormattedMessage id="constraintNotAppliedWhileErasing" />
                </Typography>
            )}
            <Box
                sx={{
                    maxHeight: 120,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {candidates.length === 0 && (
                    <Typography sx={{ ...labelSx, opacity: 0.6, py: 0.5 }}>
                        <FormattedMessage id="collisionNoStructures" />
                    </Typography>
                )}
                {candidates.map((roi) => {
                    // Can't protect the structure that's the active Inside
                    // bound (see activeBoundingRoi).
                    const isInsideBound = roi.roiNumber === activeBoundingRoi;
                    return (
                        <FormControlLabel
                            key={roi.roiNumber}
                            disabled={disabled || isInsideBound}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={
                                        !isInsideBound &&
                                        protectedRoiNumbers.includes(
                                            roi.roiNumber
                                        )
                                    }
                                    onChange={(_, checked) =>
                                        onToggleProtected(
                                            roi.roiNumber,
                                            checked
                                        )
                                    }
                                    sx={{ p: 0.5 }}
                                />
                            }
                            label={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        opacity: isInsideBound ? 0.4 : 1,
                                    }}
                                >
                                    <StructureSwatch
                                        color={dicomColorToRgbString(roi.color)}
                                    />
                                    <Typography sx={labelSx}>
                                        {roi.name}
                                    </Typography>
                                </Box>
                            }
                            sx={{ ml: 0, mr: 0 }}
                        />
                    );
                })}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={allApprovedProtected}
                            indeterminate={
                                someApprovedProtected && !allApprovedProtected
                            }
                            disabled={disabled || !hasApproved}
                            onChange={(_, checked) =>
                                onToggleProtectAllApproved(checked)
                            }
                            sx={{ p: 0.5 }}
                        />
                    }
                    label={
                        <Typography
                            sx={{
                                ...labelSx,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                // With no approved structures the option is
                                // inapplicable, so dim the whole control so it
                                // reads as prominently disabled (rather than
                                // flagging it as a warning).
                                opacity: hasApproved ? 1 : 0.4,
                            }}
                        >
                            <FormattedMessage id="collisionProtectAllApproved" />
                        </Typography>
                    }
                    sx={{ ml: 0, mr: 0 }}
                />
            </Box>
        </>
    );
}
